import { FieldCacheService, NSARouterMaker, NSARouterMakerResult, NSFRouterMaker } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { EnvService, IStorageSession } from "namirasoft-core";
import { NamirasoftPasswordServer } from "namirasoft-password";
import { NamirasoftSecretServer } from "namirasoft-secret";
import { NSNotFoundPage } from "namirasoft-site-react";
import { ReactNode, useReducer } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { PasswordLayout } from "./PasswordLayout";
import { PasswordRoutes } from "./PasswordRoutes";

// layouts
import SetupLayout from "./SetupLayout";
import DecryptMasterKeyLayout from "./components/DecryptMasterKeyLayout";
// default
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
// password
import SetupWizard from "./pages/SetupWizard";
import PasswordGeneratePage from "./pages/PasswordGeneratePage";
import PasswordRecoveryPage from "./pages/PasswordRecoveryPage";
import PasswordChangePage from "./pages/PasswordChangePage";
import QuestionsChangePage from "./pages/QuestionsChangePage";
import ImportPage from "./pages/ImportPage";
// entry
import EntryEditPage from "./pages/EntryEditPage";
import EntryListPage from "./pages/EntryListPage";
import EntryViewPage from "./pages/EntryViewPage";
// template
import TemplateEditPage from "./pages/TemplateEditPage";
import TemplateListPage from "./pages/TemplateListPage";
import TemplateViewPage from "./pages/TemplateViewPage";

function Router()
{
    let [, page_force_update] = useReducer((x) => x + 1, 0);

    let navigate = useNavigate();

    return <NSARouterMaker
        notifier_storage={new IStorageSession()}
        page_force_update={page_force_update}
        product_id={ProductUUID.NAMIRASOFT_PASSWORD}

        renderOnLogin={(result: NSARouterMakerResult) =>
        {
            let REACT_APP_BASE_URL_PASSWORD = new EnvService("REACT_APP_BASE_URL_PASSWORD", true).getString();
            let REACT_APP_BASE_URL_SECRET = new EnvService("REACT_APP_BASE_URL_SECRET", true).getString();

            let password = new NamirasoftPasswordServer(REACT_APP_BASE_URL_PASSWORD, result.account.token_manager, result.notifier.onError);
            let secret = new NamirasoftSecretServer(REACT_APP_BASE_URL_SECRET, result.account.token_manager, result.notifier.onError);

            let servers = { password, secret };

            return (
                <SetupLayout
                    {...result}
                    children={{
                        onSetupRequired: (loadUser) => <SetupWizard {...result} servers={servers} loadUser={loadUser} />,
                        onSetupAvailable: (loadUser, user) =>
                        {
                            let gate = (element: ReactNode) => (
                                <DecryptMasterKeyLayout {...result} user={user}>
                                    {element}
                                </DecryptMasterKeyLayout>
                            );

                            return (
                                <FieldCacheService
                                    {...result}
                                    render={(rows) => (
                                        <Routes>
                                            <Route path={PasswordRoutes.EntryList} element={gate(<EntryListPage {...result} servers={servers} user={user} />)} />
                                            <Route path={PasswordRoutes.EntryNew} element={gate(<EntryEditPage {...result} servers={servers} isEdit={false} user={user} />)} />
                                            <Route path={PasswordRoutes.EntryView(":id")} element={gate(<EntryViewPage {...result} servers={servers} user={user} />)} />
                                            <Route path={PasswordRoutes.EntryEdit(":id")} element={gate(<EntryEditPage {...result} servers={servers} isEdit={true} user={user} />)} />
                                            <Route path={PasswordRoutes.EntryCopy(":id")} element={gate(<EntryEditPage {...result} servers={servers} isEdit={false} user={user} />)} />

                                            <Route path={PasswordRoutes.TemplateList} element={<TemplateListPage {...result} servers={servers} />} />
                                            <Route path={PasswordRoutes.TemplateNew} element={<TemplateEditPage {...result} servers={servers} isEdit={false} />} />
                                            <Route path={PasswordRoutes.TemplateView(":id")} element={<TemplateViewPage {...result} servers={servers} />} />
                                            <Route path={PasswordRoutes.TemplateEdit(":id")} element={<TemplateEditPage {...result} servers={servers} isEdit={true} />} />
                                            <Route path={PasswordRoutes.TemplateCopy(":id")} element={<TemplateEditPage {...result} servers={servers} isEdit={false} />} />

                                            <Route path={PasswordRoutes.PasswordChange} element={<PasswordChangePage {...result} servers={servers} user={user} loadUser={loadUser} />} />
                                            <Route path={PasswordRoutes.PasswordRecovery} element={<PasswordRecoveryPage {...result} servers={servers} user={user} loadUser={loadUser} />} />
                                            <Route path={PasswordRoutes.PasswordGenerate} element={<PasswordGeneratePage {...result} servers={servers} />} />

                                            <Route path={PasswordRoutes.QuestionsChange} element={gate(<QuestionsChangePage {...result} servers={servers} user={user} loadUser={loadUser} />)} />
                                            <Route path={PasswordRoutes.Import} element={gate(<ImportPage {...result} servers={servers} user={user} />)} />

                                            <Route path={PasswordRoutes.Home} element={gate(<HomePage {...result} servers={servers} user={user} />)} />

                                            {NSFRouterMaker.getRoutes({ ...result, rows, children: <></> })}

                                            <Route path="*" element={<NSNotFoundPage />} />
                                        </Routes>
                                    )}
                                />
                            );
                        },
                    }}
                />
            );
        }}
        renderOnLogout={() => (
            <Routes>
                <Route path={PasswordRoutes.Home} element={<LoginPage />} />
                <Route path="*" element={<LoginPage />} />
            </Routes>
        )}
        navigate={navigate}
        getNSALayout={(props) => (
            <PasswordLayout {...props}>
                {props.children}
            </PasswordLayout>
        )}
        requirement={{
            verification: {
                email: true,
                phone: false
            }
        }}
    />
}

export default Router;